import React, { PropsWithChildren, useContext } from 'react';
import isUndefined from 'lodash.isundefined';
import omitBy from 'lodash.omitby';

import { SelectContext } from './context/SelectProvider';

import { ListBox, ListBoxOptionProps } from '../ListBox';
import { SelectOption } from '../types';
import { OptionProps } from '../ListBox/components/Option';

export type SelectListBoxOptionProps<T extends SelectOption> =
  PropsWithChildren<
    Omit<ListBoxOptionProps<T>, 'option'> &
      Omit<ListBoxOptionProps<T>['option'], 'label'>
  >;

const SelectListBoxOption = <T extends SelectOption>({
  children,
  className,
  ...props
}: SelectListBoxOptionProps<T>) => {
  const { enableSubLabelSearch } = useContext(SelectContext);

  const childrenArray = React.Children.toArray(children);
  if (childrenArray.length === 0) {
    return null;
  }

  const labelElement = childrenArray[0];

  if (typeof labelElement !== 'string') {
    return null;
  }

  const {
    // OptionProps
    highlightedText,
    onKeyDown,
    onSelect,
    selected,
    optionStyle,
    optionType,
    optionRef,
    // SelectOption
    color,
    disabled,
    group,
    subLabel,
    imageLoading,
    metadata,
    value,
    avatar,
    ...restProps // any other <T extends SelectOption> props passed
  } = props as T & OptionProps<T>;

  const option = {
    ...restProps,
    color,
    disabled,
    group,
    subLabel,
    imageLoading,
    metadata,
    value,
    avatar,
    label: labelElement,
  };

  const cleanOption = omitBy(option, isUndefined);

  return (
    <ListBox.Option
      {...{
        highlightedText,
        onKeyDown,
        onSelect,
        selected,
        optionStyle,
        optionType,
        optionRef,
        enableSubLabelSearch,
      }}
      disabled={disabled}
      option={cleanOption}
    />
  );
};

SelectListBoxOption.displayName = 'Option';

export default SelectListBoxOption;
